import React, { useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialRecurrenceApi } from '../api';
import {
  FINANCIAL_ENTRY_RECURRENCE_OPTIONS,
  RECURRENCE_END_MODE,
  RECURRENCE_END_MODE_OPTIONS,
  RecurrenceEndModeValue,
} from '../constants';
import { formatCalendarDateBR, parseCurrencyValue } from '../financialFormatters';
import FormOptionSelect from '../FinancialEntryForm/FormOptionSelect';
import FormCurrencyField from '../FinancialEntryForm/FormCurrencyField';
import LoadingComponent from '../../../../components/LoadingComponent';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Wrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 0.8rem;
    font-weight: 500;
    color: #374151;
  }

  input,
  select {
    height: 38px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 0 10px;
    font-size: 0.9rem;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const FooterButton = styled.button<{ $primary?: boolean }>`
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? '#2563eb' : '#d1d5db')};
  background: ${({ $primary }) => ($primary ? '#2563eb' : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : '#374151')};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface EditRecurrenceModalProps {
  recurrenceId: number;
  /** Data (YYYY-MM-DD) a partir da qual as alterações valem. */
  effectiveFrom: string;
  onClose: () => void;
  onUpdated: () => void;
}

const EditRecurrenceModal: React.FC<EditRecurrenceModalProps> = ({
  recurrenceId,
  effectiveFrom,
  onClose,
  onUpdated,
}) => {
  const { setFeedback } = useContext(FeedbackContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [frequency, setFrequency] = useState<number | null>(null);
  const [interval, setInterval] = useState('1');
  const [endMode, setEndMode] = useState<RecurrenceEndModeValue>(
    RECURRENCE_END_MODE.NEVER,
  );
  const [endDate, setEndDate] = useState('');
  const [count, setCount] = useState('');

  useEffect(() => {
    let active = true;
    financialRecurrenceApi
      .getById(recurrenceId)
      .then((rule) => {
        if (!active) return;
        setDescription(rule.description ?? '');
        setValue(rule.totalValue != null ? String(rule.totalValue) : '');
        setFrequency(rule.frequency);
        setInterval(String(rule.interval ?? 1));
        if (rule.endDate) {
          setEndMode(RECURRENCE_END_MODE.DATE);
          setEndDate(rule.endDate.slice(0, 10));
        } else if (rule.occurrenceCount != null) {
          setEndMode(RECURRENCE_END_MODE.COUNT);
          setCount(String(rule.occurrenceCount));
        }
      })
      .catch(() => {
        setFeedback({
          message: 'Erro ao carregar a recorrência.',
          isOpen: true,
          statusOk: false,
        });
        onClose();
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [recurrenceId, onClose, setFeedback]);

  const handleSave = async () => {
    if (frequency === null) {
      setFeedback({
        message: 'Selecione a frequência.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }
    if (endMode === RECURRENCE_END_MODE.DATE && !endDate) {
      setFeedback({
        message: 'Informe a data final.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }
    const countNumber = Number(count);
    if (
      endMode === RECURRENCE_END_MODE.COUNT &&
      (!Number.isInteger(countNumber) || countNumber <= 0)
    ) {
      setFeedback({
        message: 'Informe um número de ocorrências válido.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }

    const intervalNumber = Number(interval);

    try {
      setSaving(true);
      await financialRecurrenceApi.updateFrom(recurrenceId, {
        effectiveFrom,
        description: description.trim() || undefined,
        totalValue: parseCurrencyValue(value),
        frequency,
        interval:
          Number.isInteger(intervalNumber) && intervalNumber > 0
            ? intervalNumber
            : 1,
        endDate:
          endMode === RECURRENCE_END_MODE.DATE ? endDate : null,
        occurrenceCount:
          endMode === RECURRENCE_END_MODE.COUNT ? countNumber : null,
      });
      setFeedback({
        message: 'Recorrência atualizada a partir desta ocorrência.',
        isOpen: true,
        statusOk: true,
      });
      onUpdated();
      onClose();
    } catch (err: any) {
      setFeedback({
        message:
          err?.response?.data?.message || 'Erro ao atualizar a recorrência.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Wrapper onClick={(event) => event.stopPropagation()}>
        <div>
          <Title>Editar esta e as próximas</Title>
          <Subtitle>
            As alterações valem a partir de {formatCalendarDateBR(effectiveFrom)}.
            As ocorrências anteriores não mudam.
          </Subtitle>
        </div>

        {loading ? (
          <LoadingComponent />
        ) : (
          <>
            <FieldGroup>
              <label>Descrição</label>
              <input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Descrição do lançamento"
              />
            </FieldGroup>

            <Row>
              <FieldGroup>
                <label>Valor por ocorrência</label>
                <FormCurrencyField
                  id="recurrence-value"
                  name="recurrence-value"
                  value={value}
                  onChange={setValue}
                />
              </FieldGroup>

              <FormOptionSelect
                label="Frequência"
                value={frequency}
                onChange={setFrequency}
                options={FINANCIAL_ENTRY_RECURRENCE_OPTIONS}
                allowEmpty={false}
              />
            </Row>

            <Row>
              <FieldGroup>
                <label>Repetir a cada</label>
                <input
                  type="number"
                  min={1}
                  value={interval}
                  onChange={(event) => setInterval(event.target.value)}
                />
              </FieldGroup>

              <FieldGroup>
                <label>Termina</label>
                <select
                  value={endMode}
                  onChange={(event) =>
                    setEndMode(event.target.value as RecurrenceEndModeValue)
                  }
                >
                  {RECURRENCE_END_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FieldGroup>
            </Row>

            {endMode === RECURRENCE_END_MODE.DATE && (
              <FieldGroup>
                <label>Data final</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </FieldGroup>
            )}

            {endMode === RECURRENCE_END_MODE.COUNT && (
              <FieldGroup>
                <label>Número de ocorrências (a partir desta data)</label>
                <input
                  type="number"
                  min={1}
                  value={count}
                  onChange={(event) => setCount(event.target.value)}
                />
              </FieldGroup>
            )}

            <Footer>
              <FooterButton type="button" onClick={onClose}>
                Cancelar
              </FooterButton>
              <FooterButton
                type="button"
                $primary
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </FooterButton>
            </Footer>
          </>
        )}
      </Wrapper>
    </Overlay>,
    document.body,
  );
};

export default EditRecurrenceModal;
